import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Trash2, Plus, Minus, ChevronRight, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

const CartPage = () => {
  const navigate = useNavigate();
  const { items, removeFromCart, updateQuantity, cartTotal, cartCount, clearCart } = useCart();

  const shippingCost = cartTotal >= 50 ? 0 : 5.99;
  const total = cartTotal + shippingCost;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FFF9F5]" data-testid="empty-cart-page">
        <div className="container-main section-padding">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
            <p className="text-gray-600 mb-8">Looks like you haven't added any toys yet!</p>
            <Button onClick={() => navigate('/')} className="btn-primary" data-testid="start-shopping-btn">
              Start Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5]" data-testid="cart-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container-main py-4">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">Shopping Cart</span>
          </nav>
        </div>
      </div>

      <div className="container-main section-padding">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-8">
          Shopping Cart ({cartCount} items)
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
            {items.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl p-6 shadow-sm flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <Link to={`/product/${item.slug}`} className="flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-2xl"
                  />
                </Link>
                <div className="flex-1">
                  <Link to={`/product/${item.slug}`}>
                    <h3 className="font-heading font-bold text-lg text-gray-900 hover:text-brand-orange transition-colors">
                      {item.name}
                    </h3>
                  </Link>
                  <p className="text-gray-500 text-sm mb-3">{item.petType} · {item.toyType}</p>
                  
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
                        data-testid={`cart-decrease-${item.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-medium w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
                        data-testid={`cart-increase-${item.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="font-heading font-bold text-xl text-brand-orange">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            <Button
              variant="outline"
              onClick={clearCart}
              className="text-gray-500"
              data-testid="clear-cart-btn"
            >
              Clear Cart
            </Button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1" data-testid="order-summary">
            <div className="bg-white rounded-3xl p-6 shadow-sm sticky top-24">
              <h2 className="font-heading text-xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className={`font-medium ${shippingCost === 0 ? 'text-brand-green' : ''}`}>
                    {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="border-t pt-4 flex justify-between">
                  <span className="font-heading font-bold text-lg">Total</span>
                  <span className="font-heading font-bold text-2xl text-brand-orange">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              {cartTotal < 50 && (
                <div className="bg-brand-orange-light rounded-xl p-4 mb-6 flex items-start gap-3">
                  <Truck className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">
                    Add <span className="font-bold text-brand-orange">${(50 - cartTotal).toFixed(2)}</span> more to get FREE shipping!
                  </p>
                </div>
              )}

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full btn-primary mb-3"
                data-testid="proceed-checkout-btn"
              >
                Proceed to Checkout
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </Button>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-gray-500 mb-2">Secure Checkout</p>
                <div className="flex justify-center gap-4">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png"
                    alt="Visa"
                    className="h-6 opacity-60"
                  />
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png"
                    alt="Mastercard"
                    className="h-8 opacity-60"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
